import { useEffect, useState } from 'react';
import Icon from '../components/icon';
import SelectField from '../components/select-field';
import TextField from '../components/text-field';
import { createUser, generatePassword, listDivisions, listUsers } from '../api/panel-api';
import {
  emailErrorText,
  emailPattern,
  formatPhone,
  isCompletePhone,
  panelRoles,
  phoneErrorText,
  serviceRoleLabel,
  serviceRoles,
  userFormFieldErrors,
} from '../data/panel';

const accessOptions = [
  { value: 'invite', label: 'Пригласить по ссылке' },
  { value: 'explicit_password', label: 'Задать пароль вручную' },
];

const serviceOptions = serviceRoles.map((role) => (
  role.value === 'division_head' ? { value: 'rd', label: role.label } : role
));

function personName(person) {
  if (!person || typeof person !== 'object') return '';
  return [person.first_name, person.last_name].filter(Boolean).join(' ') || person.email || '';
}

function AccessCheck({ label, checked, onChange, disabled }) {
  return (
    <label className="access-check">
      <input type="checkbox" checked={checked} disabled={disabled} onChange={onChange} />
      <span>{label}</span>
    </label>
  );
}

function UserFormPage({ sections, onCancel, onCreate }) {
  const [copiedNotice, setCopiedNotice] = useState(0);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastText, setToastText] = useState('Пароль скопирован');
  const [rksName, setRksName] = useState('');
  const [divisions, setDivisions] = useState([]);
  const [formError, setFormError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [pending, setPending] = useState(false);
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    position: '',
    role: '',
    serviceRole: '',
    divisionId: '',
    access: 'invite',
    password: '',
    requirePasswordChange: true,
    sections: ['dashboard'],
    showEnvSwitcher: false,
  });

  const work = sections.filter((item) => item.group === 'work' && !item.parent_code);
  const administration = sections.filter((item) => item.group === 'administration' && !item.parent_code);
  const sectionCodes = [...work, ...administration].map((item) => item.code);
  const adminRole = form.role === 'administrator';
  const rksTaken = Boolean(rksName);
  const divisionHead = form.serviceRole === 'rd';
  const needsDivision = divisionHead || form.serviceRole === 'manager';
  const canSubmit = form.firstName && form.lastName && form.email && form.phone && form.position && form.role
    && (form.role !== 'cs_employee' || form.serviceRole)
    && (!divisionHead || form.divisionId)
    && (adminRole || form.sections.length > 0)
    && (form.access !== 'explicit_password' || form.password.length > 0)
    && !pending;

  useEffect(() => {
    if (!sections.length) return;
    setForm((current) => {
      const known = current.sections.filter((code) => sections.some((item) => item.code === code));
      if (known.length) return { ...current, sections: known };
      const hasDashboard = sections.some((item) => item.code === 'dashboard');
      return { ...current, sections: hasDashboard ? ['dashboard'] : current.sections };
    });
  }, [sections]);

  useEffect(() => {
    let cancelled = false;
    async function loadRks() {
      let page = 1;
      let total = 0;
      do {
        const data = await listUsers({ page, perPage: 100, order: 'asc', filters: {} });
        total = data.total || 0;
        const holder = (data.items || []).find((item) => item.cs_role === 'rks');
        if (holder) {
          if (!cancelled) setRksName(`${holder.first_name} ${holder.last_name}`);
          return;
        }
        page += 1;
      } while ((page - 1) * 100 < total);
    }
    loadRks().catch(() => {});
    listDivisions()
      .then((data) => {
        if (!cancelled) setDivisions(data.items || []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!copiedNotice) return undefined;
    setToastOpen(true);
    const timer = window.setTimeout(() => {
      setCopiedNotice(0);
      setToastOpen(false);
    }, 5000);
    return () => window.clearTimeout(timer);
  }, [copiedNotice]);

  function setField(key, value) {
    const nextValue = key === 'phone' ? formatPhone(value) : value;
    setForm((current) => ({ ...current, [key]: nextValue }));
    setFieldErrors((current) => ({ ...current, [key]: '' }));
    setFormError('');
  }

  function setRole(value) {
    setForm((current) => ({
      ...current,
      role: value,
      serviceRole: value === 'cs_employee' ? current.serviceRole : '',
      divisionId: value === 'cs_employee' ? current.divisionId : '',
      showEnvSwitcher: value === 'administrator'
        ? true
        : (current.role === 'administrator' ? false : current.showEnvSwitcher),
      sections: value === 'administrator' && sectionCodes.length
        ? sectionCodes
        : (current.role === 'administrator'
          ? (sectionCodes.includes('dashboard') ? ['dashboard'] : [])
          : current.sections),
    }));
  }

  function setServiceRole(value) {
    setForm((current) => ({
      ...current,
      serviceRole: value,
      divisionId: value === 'rd' || value === 'manager' ? current.divisionId : '',
    }));
    setFieldErrors((current) => ({ ...current, serviceRole: '', divisionId: '' }));
  }

  async function fillPassword() {
    setCopiedNotice(0);
    setToastOpen(false);
    setFormError('');
    try {
      const data = await generatePassword();
      setField('password', data.password);
    } catch (error) {
      setFormError(error.message);
    }
  }

  async function copyPassword() {
    if (!form.password) return;
    let copied = false;
    try {
      await navigator.clipboard.writeText(form.password);
      copied = true;
    } catch {
      const area = document.createElement('textarea');
      area.value = form.password;
      area.setAttribute('readonly', '');
      area.style.position = 'fixed';
      area.style.opacity = '0';
      document.body.appendChild(area);
      area.select();
      copied = document.execCommand('copy');
      area.remove();
    }
    if (copied) {
      setToastText('Пароль скопирован');
      setCopiedNotice((count) => count + 1);
    }
  }

  function toggleSection(code) {
    if (adminRole) return;
    setForm((current) => ({
      ...current,
      sections: current.sections.includes(code)
        ? current.sections.filter((item) => item !== code)
        : [...current.sections, code],
    }));
  }

  async function submit() {
    if (!canSubmit) return;
    const nextFieldErrors = {};
    if (!emailPattern.test(form.email)) nextFieldErrors.email = emailErrorText;
    if (!isCompletePhone(form.phone)) nextFieldErrors.phone = phoneErrorText;
    if (Object.keys(nextFieldErrors).length) {
      setFieldErrors(nextFieldErrors);
      return;
    }
    setPending(true);
    setFormError('');
    setFieldErrors({});
    const body = {
      first_name: form.firstName,
      last_name: form.lastName,
      email: form.email,
      phone: form.phone,
      job_title: form.position,
      role: form.role,
      sections: form.sections,
      access_method: form.access,
    };
    body.can_see_environment_switcher = adminRole ? true : form.showEnvSwitcher;
    if (adminRole && sectionCodes.length) body.sections = sectionCodes;
    if (form.role === 'cs_employee') body.cs_role = form.serviceRole;
    if (divisionHead || (form.serviceRole === 'manager' && form.divisionId)) {
      body.division_id = form.divisionId;
    }
    if (form.access === 'explicit_password') {
      body.password = form.password;
      body.must_change_password = form.requirePasswordChange;
    }
    try {
      const created = await createUser(body);
      if (created && created.mail_sent === false && created.mail_warning) {
        setToastText(created.mail_warning);
        setToastOpen(true);
        window.setTimeout(() => onCreate(), 1600);
        return;
      }
      onCreate();
    } catch (error) {
      const mapped = userFormFieldErrors(error);
      setFormError(mapped.banner);
      setFieldErrors(mapped.fields);
    } finally {
      setPending(false);
    }
  }

  const serviceChoices = serviceOptions.map((role) => {
    if (role.value !== 'rks' || !rksTaken) return role;
    return { ...role, label: 'Руководитель КС', note: rksName, disabled: true };
  });
  const divisionChoices = divisions.length
    ? divisions.map((item) => {
      const taken = item.head_user_id;
      const head = personName(item.head);
      return {
        value: item.id,
        label: item.title,
        note: taken && head ? head : undefined,
        disabled: Boolean(taken),
      };
    })
    : [{ value: '', label: 'Нет дивизионов', disabled: true }];

  return (
    <section className="content content--plain content--stack" aria-label="Новый пользователь">
      {toastOpen ? (
        <div className="toast" role="status">
          <span className="toast__icon">
            <Icon name="check-circle" size={18} tone="inherit" />
          </span>
          <p className="toast__title">{toastText}</p>
          <button type="button" className="toast__close" aria-label="Закрыть" onClick={() => setToastOpen(false)}>
            <Icon name="x" size={14} tone="inherit" />
          </button>
        </div>
      ) : null}
      <button type="button" className="page-back" onClick={onCancel}>
        <Icon name="chevron-left" size={14} tone="inherit" />
        Пользователи
      </button>
      <article className="card user-form">
        <div className="card__head">
          <h1 className="card-title">Новый пользователь</h1>
          {form.serviceRole ? (
            <span className="user-form__role-tag">{serviceRoleLabel[form.serviceRole] || ''}</span>
          ) : null}
        </div>
        <div className="card__body">
          {formError ? <p className="empty">{formError}</p> : null}
          <div className="field-row">
            <TextField label="Имя" value={form.firstName} error={fieldErrors.firstName} onChange={(value) => setField('firstName', value)} />
            <TextField label="Фамилия" value={form.lastName} error={fieldErrors.lastName} onChange={(value) => setField('lastName', value)} />
          </div>
          <div className="field-row">
            <TextField label="Электронная почта" value={form.email} error={fieldErrors.email} onChange={(value) => setField('email', value)} />
            <TextField label="Телефон" value={form.phone} error={fieldErrors.phone} onChange={(value) => setField('phone', value)} />
          </div>
          <div className="field-row field-row--half">
            <TextField label="Должность" value={form.position} error={fieldErrors.position} onChange={(value) => setField('position', value)} />
          </div>
          <div className="user-form__roles">
            <div className="user-form__role">
              <SelectField
                label="Роль в панели"
                value={form.role}
                onChange={setRole}
                options={panelRoles}
              />
            </div>
            {form.role === 'cs_employee' ? (
              <div className="user-form__role">
                <SelectField
                  label="Роль в клиентской службе"
                  value={form.serviceRole}
                  onChange={setServiceRole}
                  options={serviceChoices}
                />
              </div>
            ) : null}
            {needsDivision ? (
              <div className="user-form__role">
                <SelectField
                  label="Дивизион"
                  value={form.divisionId}
                  onChange={(value) => setField('divisionId', value)}
                  options={divisionChoices}
                />
              </div>
            ) : null}
          </div>
          <div className="manual-password">
            <div className="manual-password__row">
              <div className="manual-password__method">
                <SelectField
                  label="Как выдаётся доступ"
                  required={false}
                  value={form.access}
                  onChange={(value) => setField('access', value)}
                  options={accessOptions}
                />
              </div>
              {form.access === 'explicit_password' ? (
                <div className="manual-password__tools">
                  <TextField
                    label="Пароль"
                    value={form.password}
                    autoComplete="off"
                    error={fieldErrors.password}
                    onChange={(value) => {
                      setCopiedNotice(0);
                      setToastOpen(false);
                      setField('password', value);
                    }}
                  />
                  <button type="button" className="btn btn--secondary" onClick={fillPassword}>
                    <Icon name="sync" size={16} />
                    Сгенерировать
                  </button>
                  <button
                    type="button"
                    className={copiedNotice ? 'btn btn--secondary btn--copied' : 'btn btn--secondary'}
                    onClick={copyPassword}
                    disabled={!form.password}
                  >
                    <Icon name={copiedNotice ? 'check' : 'copy'} size={16} tone={copiedNotice ? 'inherit' : undefined} />
                    {copiedNotice ? 'Скопировано' : 'Скопировать'}
                  </button>
                </div>
              ) : null}
            </div>
            {form.access === 'explicit_password' ? (
              <label className="switch">
                <input
                  className="switch__input"
                  type="checkbox"
                  checked={form.requirePasswordChange}
                  onChange={(event) => setField('requirePasswordChange', event.target.checked)}
                />
                <span className="switch__track" aria-hidden="true">
                  <span className="switch__knob" />
                </span>
                Запросить смену пароля при первом входе
              </label>
            ) : null}
          </div>
          <div className="section-access">
            <h2 className="section-access__title">Открытые разделы</h2>
            {fieldErrors.sections ? <p className="empty">{fieldErrors.sections}</p> : null}
            <div className="section-access__columns">
              <div className="section-access__col">
                <p className="section-access__heading">Работа</p>
                {work.map((item) => (
                  <AccessCheck
                    key={item.code}
                    label={item.title}
                    checked={adminRole || form.sections.includes(item.code)}
                    disabled={adminRole}
                    onChange={() => toggleSection(item.code)}
                  />
                ))}
              </div>
              <div className="section-access__col">
                <p className="section-access__heading">Администрирование</p>
                {administration.map((item) => (
                  <AccessCheck
                    key={item.code}
                    label={item.title}
                    checked={adminRole || form.sections.includes(item.code)}
                    disabled={adminRole}
                    onChange={() => toggleSection(item.code)}
                  />
                ))}
                <AccessCheck
                  label="Видеть переключатель окружения"
                  checked={adminRole || form.showEnvSwitcher}
                  disabled={adminRole}
                  onChange={() => setField('showEnvSwitcher', !form.showEnvSwitcher)}
                />
              </div>
            </div>
            {adminRole ? (
              <div className="note note--info">
                <Icon name="info" size={20} />
                <p>Администратору открыты все разделы, включая те, что появятся позже.</p>
              </div>
            ) : null}
            {form.role === 'guest' ? (
              <div className="note note--info">
                <Icon name="info" size={20} />
                <p>Гость видит выбранные разделы, но ничего в них не делает: не меняет данные, не назначает менеджеров и не комментирует.</p>
              </div>
            ) : null}
          </div>
        </div>
        <div className="form-actions" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn--secondary" onClick={onCancel}>Отмена</button>
          <button type="button" className="btn" disabled={!canSubmit} onClick={submit}>Добавить</button>
        </div>
      </article>
    </section>
  );
}

export default UserFormPage;
